const CSV_BOM = '\uFEFF';

const encodeText = (value) => {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(value);
  const text = unescape(encodeURIComponent(value));
  const bytes = new Uint8Array(text.length);
  for (let index = 0; index < text.length; index += 1) {
    bytes[index] = text.charCodeAt(index);
  }
  return bytes;
};

export const sanitizeCsvFilename = (name, fallback = 'result') => {
  const cleaned = String(name || fallback)
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_');
  return cleaned || fallback;
};

export const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const escapeCsvValue = (value) => {
  const text = csvCell(value);
  if (/[",\r\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

export const buildCsvContent = (rows = [], headers = []) => {
  const finalHeaders = headers.length
    ? headers
    : Array.from(rows.reduce((keys, row) => {
        Object.keys(row || {}).forEach((key) => keys.add(key));
        return keys;
      }, new Set()));

  const lines = [
    finalHeaders.map(escapeCsvValue).join(','),
    ...rows.map((row) => finalHeaders.map((header) => escapeCsvValue(row?.[header])).join(',')),
  ];

  return `${CSV_BOM}${lines.join('\r\n')}`;
};

export const buildCsvMatrixContent = (matrix = []) => (
  `${CSV_BOM}${matrix.map((row) => (row || []).map(escapeCsvValue).join(',')).join('\r\n')}`
);

export const buildReadableCsvContent = ({
  matrix,
  metaRows = [],
  metaHeaders = ['항목', '내용'],
  rows = [],
  headers = [],
} = {}) => {
  if (matrix) return buildCsvMatrixContent(matrix);
  if (!metaRows.length) return buildCsvContent(rows, headers);
  const metaCsv = buildCsvContent(metaRows, metaHeaders).replace(CSV_BOM, '');
  const tableCsv = buildCsvContent(rows, headers).replace(CSV_BOM, '');
  return `${CSV_BOM}${metaCsv}\r\n\r\n${tableCsv}`;
};

const xmlEscape = (value) => csvCell(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const normalizeSheetName = (name, index) => {
  const cleaned = String(name || `Sheet${index + 1}`)
    .replace(/[\][:*?/\\]/g, ' ')
    .trim()
    .slice(0, 31);
  return cleaned || `Sheet${index + 1}`;
};

const getHeaders = (rows = [], headers = []) => (
  headers.length
    ? headers
    : Array.from(rows.reduce((keys, row) => {
        Object.keys(row || {}).forEach((key) => keys.add(key));
        return keys;
      }, new Set()))
);

const columnName = (index) => {
  let value = index + 1;
  let name = '';
  while (value > 0) {
    const mod = (value - 1) % 26;
    name = String.fromCharCode(65 + mod) + name;
    value = Math.floor((value - mod) / 26);
  }
  return name;
};

const buildWorksheetXml = (rows = [], headers = [], columnWidths = []) => {
  const finalHeaders = getHeaders(rows, headers);
  const allRows = [finalHeaders, ...rows.map((row) => finalHeaders.map((header) => row?.[header]))];
  return buildWorksheetXmlFromMatrix(allRows, columnWidths);
};

const buildWorksheetXmlFromMatrix = (matrix = [], columnWidths = []) => {
  const columnCount = matrix.reduce((max, row) => Math.max(max, (row || []).length), 0);
  const rowXml = matrix.map((row, rowIndex) => {
    const cells = row.map((value, columnIndex) => {
      const ref = `${columnName(columnIndex)}${rowIndex + 1}`;
      return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xmlEscape(value)}</t></is></c>`;
    }).join('');
    return `<row r="${rowIndex + 1}">${cells}</row>`;
  }).join('');
  const colXml = Array.from({ length: columnCount }).map((_, index) => {
    const maxLength = Math.max(
      ...matrix.map((row) => csvCell(row?.[index]).length),
    );
    const maxWidth = columnWidths[index] || 60;
    const width = Math.min(Math.max(maxLength + 2, 10), maxWidth);
    return `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`;
  }).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <cols>${colXml}</cols>
  <sheetData>${rowXml}</sheetData>
</worksheet>`;
};

const buildWorkbookXml = (sheets) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    ${sheets.map((sheet, index) => `<sheet name="${xmlEscape(sheet.name)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`).join('')}
  </sheets>
</workbook>`;

const buildWorkbookRelsXml = (sheets) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${sheets.map((_, index) => `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`).join('')}
</Relationships>`;

const buildContentTypesXml = (sheets) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  ${sheets.map((_, index) => `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')}
</Types>`;

const ROOT_RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xEDB88320 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xFFFFFFFF;
  bytes.forEach((byte) => {
    crc = CRC_TABLE[(crc ^ byte) & 0xFF] ^ (crc >>> 8);
  });
  return (crc ^ 0xFFFFFFFF) >>> 0;
};

const writeUint16 = (view, offset, value) => view.setUint16(offset, value, true);
const writeUint32 = (view, offset, value) => view.setUint32(offset, value >>> 0, true);

const dateToDos = (date = new Date()) => {
  const year = Math.max(date.getFullYear(), 1980);
  return {
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2),
    date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  };
};

const concatBytes = (chunks) => {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const output = new Uint8Array(total);
  let offset = 0;
  chunks.forEach((chunk) => {
    output.set(chunk, offset);
    offset += chunk.length;
  });
  return output;
};

const createZipBlob = (files) => {
  const localChunks = [];
  const centralChunks = [];
  let localOffset = 0;
  const { time, date } = dateToDos();

  files.forEach((file) => {
    const nameBytes = encodeText(file.name);
    const dataBytes = encodeText(file.content);
    const crc = crc32(dataBytes);

    const localHeader = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(localHeader.buffer);
    writeUint32(localView, 0, 0x04034B50);
    writeUint16(localView, 4, 20);
    writeUint16(localView, 6, 0x0800);
    writeUint16(localView, 8, 0);
    writeUint16(localView, 10, time);
    writeUint16(localView, 12, date);
    writeUint32(localView, 14, crc);
    writeUint32(localView, 18, dataBytes.length);
    writeUint32(localView, 22, dataBytes.length);
    writeUint16(localView, 26, nameBytes.length);
    writeUint16(localView, 28, 0);
    localHeader.set(nameBytes, 30);
    localChunks.push(localHeader, dataBytes);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    writeUint32(centralView, 0, 0x02014B50);
    writeUint16(centralView, 4, 20);
    writeUint16(centralView, 6, 20);
    writeUint16(centralView, 8, 0x0800);
    writeUint16(centralView, 10, 0);
    writeUint16(centralView, 12, time);
    writeUint16(centralView, 14, date);
    writeUint32(centralView, 16, crc);
    writeUint32(centralView, 20, dataBytes.length);
    writeUint32(centralView, 24, dataBytes.length);
    writeUint16(centralView, 28, nameBytes.length);
    writeUint16(centralView, 30, 0);
    writeUint16(centralView, 32, 0);
    writeUint16(centralView, 34, 0);
    writeUint16(centralView, 36, 0);
    writeUint32(centralView, 38, 0);
    writeUint32(centralView, 42, localOffset);
    centralHeader.set(nameBytes, 46);
    centralChunks.push(centralHeader);

    localOffset += localHeader.length + dataBytes.length;
  });

  const centralDirectory = concatBytes(centralChunks);
  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);
  writeUint32(endView, 0, 0x06054B50);
  writeUint16(endView, 4, 0);
  writeUint16(endView, 6, 0);
  writeUint16(endView, 8, files.length);
  writeUint16(endView, 10, files.length);
  writeUint32(endView, 12, centralDirectory.length);
  writeUint32(endView, 16, localOffset);
  writeUint16(endView, 20, 0);

  return new Blob([concatBytes([...localChunks, centralDirectory, endRecord])], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

export const buildXlsxBlob = (sheets = []) => {
  const normalizedSheets = sheets.map((sheet, index) => ({
    name: normalizeSheetName(sheet.name, index),
    matrix: sheet.matrix,
    columnWidths: sheet.columnWidths || [],
    rows: sheet.rows || [],
    headers: getHeaders(sheet.rows || [], sheet.headers || []),
  })).filter((sheet) => (sheet.matrix ? sheet.matrix.length > 0 : sheet.headers.length > 0));
  const finalSheets = normalizedSheets.length ? normalizedSheets : [{ name: 'Sheet1', rows: [], headers: [] }];
  const files = [
    { name: '[Content_Types].xml', content: buildContentTypesXml(finalSheets) },
    { name: '_rels/.rels', content: ROOT_RELS_XML },
    { name: 'xl/workbook.xml', content: buildWorkbookXml(finalSheets) },
    { name: 'xl/_rels/workbook.xml.rels', content: buildWorkbookRelsXml(finalSheets) },
    ...finalSheets.map((sheet, index) => ({
      name: `xl/worksheets/sheet${index + 1}.xml`,
      content: sheet.matrix
        ? buildWorksheetXmlFromMatrix(sheet.matrix, sheet.columnWidths)
        : buildWorksheetXml(sheet.rows, sheet.headers, sheet.columnWidths),
    })),
  ];
  return createZipBlob(files);
};

export const downloadCsvFile = ({ filename, rows, headers, metaRows, metaHeaders, matrix }) => {
  const csv = buildReadableCsvContent({ rows, headers, metaRows, metaHeaders, matrix });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, `${sanitizeCsvFilename(filename)}.csv`);
};

export const downloadXlsxFile = ({ filename, sheets }) => {
  downloadBlob(buildXlsxBlob(sheets), `${sanitizeCsvFilename(filename)}.xlsx`);
};

export const downloadExportFile = ({ filename, format, rows, headers, sheets, metaRows, metaHeaders, matrix }) => {
  if (format === 'xlsx') {
    downloadXlsxFile({
      filename,
      sheets: sheets || [{ name: '결과', rows, headers, matrix }],
    });
    return;
  }
  downloadCsvFile({ filename, rows, headers, metaRows, metaHeaders, matrix });
};

const downloadBlob = (blob, downloadName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = downloadName;
  link.click();
  URL.revokeObjectURL(url);
};
